import React, { useState, useEffect, useRef } from "react";
import {
  CardContent,
  Grid,
  Card,
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import axios from "axios";
import qs from "qs";
import { Line } from "react-chartjs-2";
import { useDispatch, useSelector } from "react-redux";
import { setSubmitted } from "redux/productParams";
import { updateProductloader } from "redux/loaders";
import { setChartData, resetChartData } from "redux/productParams";
import { Bars } from "react-loader-spinner";
import Backdrop from "@mui/material/Backdrop";

const StoreProductSales = ({
  startDate,
  endDate,
  stores: propStores,
  allStores: propAllStores,
}) => {
  const choices = useSelector((state) => state.productSales);
  const allMultipleSelects = useSelector((state) => state.stores);
  const component =
    allMultipleSelects.findIndex(
      (el) => el.componentName === "products.stores",
    ) == -1
      ? 0
      : allMultipleSelects.findIndex(
          (el) => el.componentName === "products.stores",
        );
  const effectiveStores =
    propStores !== undefined
      ? propStores
      : allMultipleSelects[component]?.items || [];
  const effectiveAllStores =
    propAllStores !== undefined
      ? propAllStores
      : !!allMultipleSelects[component]?.allItemsSelected;

  const loader = useSelector((state) => state.loaders.productLoader);
  const submitPressed = useSelector((state) => state.productSales.submitted);
  const dispatcher = useDispatch();
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState([]);
  const [chart, setChart] = useState({
    amount: { labels: [], datasets: [] },
    quantity: { labels: [], datasets: [] },
  });
  const [isAllStores, setIsAllStores] = useState(true);
  const [orderBy, setOrderBy] = useState("product_name");
  const [order, setOrder] = useState("asc");

  const IfMounted = () => {
    const isMountRef = useRef(true);
    useEffect(() => {
      isMountRef.current = false;
    }, []);
    return isMountRef.current;
  };
  const didMounted = IfMounted();

  useEffect(() => {
    const loadData = async () => {
      if (!choices.products || choices.products.length === 0) {
        setRows([]);
        setTotals([]);
        setChart({
          amount: { labels: [], datasets: [] },
          quantity: { labels: [], datasets: [] },
        });
        return;
      }
      dispatcher(updateProductloader(true));
      try {
        const result = await axios.get(
          "/api/productStoreSales/product_sales_table/",
          {
            params: {
              startDate: startDate,
              endDate: endDate,
              products: choices.products,
              storeId: effectiveStores,
              allStores: effectiveAllStores,
            },
            paramsSerializer: (params) =>
              qs.stringify(params, { arrayFormat: "repeat" }),
          },
        );
        const data = result.data || {};
        setRows(data.rows || []);
        const sortedTotals = (data.totals || []).slice().sort((a, b) => {
          if (a.product_name < b.product_name) return -1;
          if (a.product_name > b.product_name) return 1;
          if ((a.store || "") < (b.store || "")) return -1;
          if ((a.store || "") > (b.store || "")) return 1;
          return 0;
        });
        setTotals(sortedTotals);
        setChart(
          data.chart || {
            amount: { labels: [], datasets: [] },
            quantity: { labels: [], datasets: [] },
          },
        );
        setIsAllStores(!!data.allStores);
        dispatcher(
          setChartData({
            amount: data.chart?.amount || { labels: [], datasets: [] },
            quantity: data.chart?.quantity || { labels: [], datasets: [] },
            total_sales: data.totals || [],
          }),
        );
      } catch (e) {
        setRows([]);
        setTotals([]);
        setChart({
          amount: { labels: [], datasets: [] },
          quantity: { labels: [], datasets: [] },
        });
        dispatcher(resetChartData());
      } finally {
        dispatcher(updateProductloader(false));
        dispatcher(setSubmitted(false));
      }
    };
    if (!didMounted) {
      loadData();
    }
  }, [submitPressed]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedRows = React.useMemo(() => {
    const combined = [...rows];
    combined.sort((a, b) => {
      if (a.product_name < b.product_name) return order === "asc" ? -1 : 1;
      if (a.product_name > b.product_name) return order === "asc" ? 1 : -1;
      if ((a.store || "") < (b.store || "")) return order === "asc" ? -1 : 1;
      if ((a.store || "") > (b.store || "")) return order === "asc" ? 1 : -1;
      const da = a.date || "";
      const db = b.date || "";
      if (da < db) return order === "asc" ? -1 : 1;
      if (da > db) return order === "asc" ? 1 : -1;
      return 0;
    });
    if (orderBy !== "product_name") {
      combined.sort((a, b) => {
        const av = a[orderBy];
        const bv = b[orderBy];
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        if (av < bv) return order === "asc" ? -1 : 1;
        if (av > bv) return order === "asc" ? 1 : -1;
        return 0;
      });
    }
    return combined;
  }, [rows, order, orderBy]);

  const headCells = isAllStores
    ? [
        { id: "product_name", label: "Product" },
        { id: "date", label: "Date" },
        { id: "quantity", label: "Quantity", numeric: true },
        { id: "amount", label: "Amount", numeric: true },
      ]
    : [
        { id: "product_name", label: "Product" },
        { id: "store", label: "Store" },
        { id: "date", label: "Date" },
        { id: "quantity", label: "Quantity", numeric: true },
        { id: "amount", label: "Amount", numeric: true },
      ];

  const colSpan = headCells.length;
  return (
    <Box sx={{ marginRight: 3, marginLeft: 3, marginTop: 2 }}>
      {loader && (
        <Backdrop style={{ zIndex: 2 }} open={true}>
          <Bars color="#862D23" height="100" width="100" />
        </Backdrop>
      )}

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Product Sales Table
          </Typography>
          <Typography variant="caption" color="textSecondary" gutterBottom>
            {isAllStores
              ? "All stores aggregated – totals per product"
              : "Sorted by product, store and date. Totals per product/store shown below."}
          </Typography>
          <TableContainer
            component={Paper}
            sx={{ marginTop: 2, maxHeight: 600 }}
          >
            <Table stickyHeader size="small" aria-label="product sales table">
              <TableHead>
                <TableRow>
                  {headCells.map((hc) => (
                    <TableCell
                      key={hc.id}
                      align={hc.numeric ? "right" : "left"}
                      sortDirection={orderBy === hc.id ? order : false}
                    >
                      <TableSortLabel
                        active={orderBy === hc.id}
                        direction={orderBy === hc.id ? order : "asc"}
                        onClick={() => handleRequestSort(hc.id)}
                      >
                        {hc.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={colSpan} align="center">
                      No data – select products and submit
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedRows.map((r, idx) => (
                    <TableRow
                      key={`${r.product_id}-${r.store_number}-${r.date}-${idx}`}
                      hover
                    >
                      <TableCell>{r.product_name}</TableCell>
                      {!isAllStores && <TableCell>{r.store}</TableCell>}
                      <TableCell>{r.date}</TableCell>
                      <TableCell align="right">
                        {Number(r.quantity).toFixed(2)}
                      </TableCell>
                      <TableCell align="right">
                        {Number(r.amount).toFixed(2)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
                {totals.map((t, idx) => (
                  <TableRow
                    key={`total-${t.product_id}-${t.store_number}-${idx}`}
                    sx={{ backgroundColor: "#f5f5f5", fontWeight: "bold" }}
                  >
                    <TableCell sx={{ fontWeight: "bold" }}>
                      {t.product_name}
                    </TableCell>
                    {!isAllStores && (
                      <TableCell sx={{ fontWeight: "bold" }}>
                        {t.store} (Total)
                      </TableCell>
                    )}
                    <TableCell sx={{ fontWeight: "bold" }}>
                      {isAllStores ? "Total" : "-"}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: "bold" }}>
                      {Number(t.quantity).toFixed(2)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: "bold" }}>
                      {Number(t.amount).toFixed(2)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {(chart.amount.datasets.length > 0 ||
        chart.quantity.datasets.length > 0) && (
        <Card sx={{ marginTop: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {isAllStores
                ? "Sales per Product (All Stores)"
                : "Sales per Product and Store"}
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="subtitle2">Amount</Typography>
                <Box sx={{ height: 350 }}>
                  <Line
                    data={chart.amount}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: { legend: { position: "top" } },
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2">Quantity</Typography>
                <Box sx={{ height: 350 }}>
                  <Line
                    data={chart.quantity}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: { legend: { position: "top" } },
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};
export default StoreProductSales;
